import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../services/api'
import { PageHeader, Card, Badge } from '../components/ui'

interface DishRating {
  id: string
  stars: number
  comment: string | null
  phone: string | null
  bonusPoints: number
  createdAt: string
  dish: { id: string; name: string }
  order: { id: string; createdAt: string; table: { number: number } }
}

interface DishSummary {
  dishId: string
  dishName: string
  avg: number
  total: number
}

function StarDisplay({ value }: { value: number }) {
  return (
    <span className="text-amber-400 tracking-tight">
      {[1,2,3,4,5].map((n) => (
        <span key={n} style={{ opacity: n <= value ? 1 : 0.25 }}>★</span>
      ))}
    </span>
  )
}

function avgColor(avg: number) {
  if (avg >= 4.5) return 'green'
  if (avg >= 3.5) return 'yellow'
  return 'red'
}

export default function RatingsPage() {
  const [selectedDish, setSelectedDish] = useState<DishSummary | null>(null)

  const { data: ratings = [], isLoading } = useQuery<DishRating[]>({
    queryKey: ['ratings'],
    queryFn: async () => (await api.get('/ratings')).data,
    refetchInterval: 30_000,
  })

  // Agrupar por platillo
  const byDish = ratings.reduce<Record<string, DishSummary & { ratings: DishRating[] }>>((acc, r) => {
    if (!acc[r.dish.id]) {
      acc[r.dish.id] = { dishId: r.dish.id, dishName: r.dish.name, avg: 0, total: 0, ratings: [] }
    }
    acc[r.dish.id].ratings.push(r)
    acc[r.dish.id].total++
    return acc
  }, {})

  Object.values(byDish).forEach((d) => {
    d.avg = d.ratings.reduce((acc, r) => acc + r.stars, 0) / d.total
  })

  const dishes = Object.values(byDish).sort((a, b) => b.avg - a.avg)

  const selectedRatings = selectedDish
    ? (byDish[selectedDish.dishId]?.ratings ?? [])
    : ratings

  const globalAvg = ratings.length
    ? ratings.reduce((acc, r) => acc + r.stars, 0) / ratings.length
    : null

  if (isLoading) return <div className="p-8 text-sm text-gray-400">Cargando…</div>

  return (
    <div className="p-8 space-y-6">
      <PageHeader
        title="Evaluaciones"
        subtitle="Retroalimentación de los comensales por platillo"
      />

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="p-5">
          <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Promedio general</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">
            {globalAvg ? globalAvg.toFixed(1) : '—'}
          </p>
          {globalAvg && <StarDisplay value={Math.round(globalAvg)} />}
        </Card>
        <Card className="p-5">
          <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Total evaluaciones</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{ratings.length}</p>
        </Card>
        <Card className="p-5">
          <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Platillos evaluados</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{dishes.length}</p>
        </Card>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Columna izquierda: resumen por platillo */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Por platillo</h3>
          <button
            onClick={() => setSelectedDish(null)}
            className={`w-full text-left px-4 py-3 rounded-lg border transition-colors
              ${!selectedDish ? 'border-gray-900 bg-gray-50' : 'border-gray-100 hover:bg-gray-50'}`}
          >
            <span className="font-medium text-sm text-gray-900">Todos los platillos</span>
            <span className="ml-2 text-xs text-gray-400">{ratings.length} eval.</span>
          </button>
          {dishes.map((d) => (
            <button
              key={d.dishId}
              onClick={() => setSelectedDish(d)}
              className={`w-full text-left px-4 py-3 rounded-lg border transition-colors
                ${selectedDish?.dishId === d.dishId ? 'border-gray-900 bg-gray-50' : 'border-gray-100 hover:bg-gray-50'}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm text-gray-900 truncate">{d.dishName}</span>
                <Badge label={d.avg.toFixed(1)} color={avgColor(d.avg)} />
              </div>
              <div className="flex items-center gap-2 mt-1">
                <StarDisplay value={Math.round(d.avg)} />
                <span className="text-xs text-gray-400">{d.total} eval.</span>
              </div>
            </button>
          ))}
          {dishes.length === 0 && (
            <p className="text-sm text-gray-400 px-4">Sin evaluaciones aún</p>
          )}
        </div>

        {/* Columna derecha: listado de evaluaciones */}
        <div className="col-span-2 space-y-3">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
            {selectedDish ? selectedDish.dishName : 'Todas las evaluaciones'}
            <span className="ml-2 text-gray-400 normal-case font-normal">({selectedRatings.length})</span>
          </h3>
          {selectedRatings.length === 0 && (
            <Card className="p-8 text-center text-sm text-gray-400">Sin evaluaciones</Card>
          )}
          {selectedRatings.map((r) => (
            <Card key={r.id} className="p-4 space-y-1">
              <div className="flex items-start justify-between">
                <div>
                  <StarDisplay value={r.stars} />
                  {!selectedDish && (
                    <span className="ml-2 text-xs font-semibold text-gray-700">{r.dish.name}</span>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-400">
                    Mesa {r.order.table.number} · {new Date(r.createdAt).toLocaleString('es-MX', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                  {r.phone && <p className="text-xs text-gray-400 mt-0.5">{r.phone}</p>}
                </div>
              </div>
              {r.comment && (
                <p className="text-sm text-gray-600 italic">"{r.comment}"</p>
              )}
              {r.bonusPoints > 0 && (
                <p className="text-xs text-amber-600">+{r.bonusPoints} puntos acumulados</p>
              )}
            </Card>
          ))}
        </div>
      </div>
    </div>
  )
}
